"use client";
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import axios from "axios";
import {
  Form, Input, Select, Upload, message, Spin,
  Row, Col, Button, Drawer, Tabs, Tooltip
} from "antd";
import { UploadOutlined, PlusOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import App from "../../Utils/LayoutMobile";

// ── Environment Variables ──
const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:3500";

// ── Admin Theme Constants ──
const C_PINK      = "#F04DA0";
const C_PURPLE    = "#8B2FC9";
const C_GRAD      = `linear-gradient(135deg, ${C_PINK}, ${C_PURPLE})`;
const C_SOFT_BG   = "#F8F4FF";
const C_TEXT_MAIN = "#1a0040";
const C_TEXT_MUTED= "#64748B";

// ── Admin Icons ──
const SaveIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/>
    <polyline points="7 3 7 8 15 8"/>
  </svg>
);
const GlobeIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

// ── Website Section SVG Assets ──
const StarIcon = ({ filled }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "#c026d3" : "none"} stroke="#c026d3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
const QuoteIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M4 20C4 14.477 8.477 10 14 10v3c-3.314 0-6 2.686-6 6v8H4V20Z" fill="#c026d3" opacity="0.15"/>
    <path d="M18 20C18 14.477 22.477 10 28 10v3c-3.314 0-6 2.686-6 6v8h-4V20Z" fill="#c026d3" opacity="0.15"/>
    <path d="M4 20C4 14.477 8.477 10 14 10v3c-3.314 0-6 2.686-6 6v8H4V20Z" stroke="#c026d3" strokeWidth="1.5"/>
    <path d="M18 20C18 14.477 22.477 10 28 10v3c-3.314 0-6 2.686-6 6v8h-4V20Z" stroke="#c026d3" strokeWidth="1.5"/>
  </svg>
);
const BlobBg = () => (
  <svg className="wa-blob" viewBox="0 0 100 100" fill="none">
    <path d="M82.1 33.5C87.4 42.4 87.8 54.3 82.5 64C77.2 73.7 66.2 81.2 54.2 84.4C42.2 87.6 29.2 86.5 19.8 79.5C10.4 72.5 4.6 59.6 4.2 46.8C3.8 34 8.8 21.3 17.6 13.5C26.4 5.7 39 2.8 50.8 3.5C62.6 4.2 76.8 24.6 82.1 33.5Z" fill="#ffffff" />
  </svg>
);
const GalleryIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C_PINK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);
const ChevLeft  = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>;
const ChevRight = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>;
const CloseIcon = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const ZoomIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>;

// ── Why Attend: 5 selectable icons ──
const waIcons = [
  <svg key="star" width="36" height="36" viewBox="0 0 24 24" fill="none">
    <defs><linearGradient id="wa-g0" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor={C_PINK}/><stop offset="100%" stopColor={C_PURPLE}/></linearGradient></defs>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill="url(#wa-g0)" stroke="none"/>
  </svg>,
  <svg key="hands" width="36" height="36" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" stroke={C_PURPLE} strokeWidth="1.5"/>
    <path d="M14 4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" stroke={C_PURPLE} strokeWidth="1.5"/>
    <path d="M10 4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" stroke={C_PURPLE} strokeWidth="1.5"/>
    <path d="M10 11V6M14 11V6M18 11V9" stroke={C_PURPLE} strokeWidth="1.5"/>
    <path d="M6 13v-2a2 2 0 0 1 2-2v0a2 2 0 0 1 2 2v0" stroke={C_PINK} strokeWidth="1.5"/>
    <path d="M10 13V9" stroke={C_PINK} strokeWidth="1.5"/>
    <path d="M10 16.5A5.5 5.5 0 0 1 4.5 11" stroke={C_PINK} strokeWidth="1.5"/>
    <path d="M20.5 11A5.5 5.5 0 0 1 15 16.5h-5" stroke={C_PINK} strokeWidth="1.5"/>
    <circle cx="19" cy="5" r="3" fill={C_PINK}/>
    <path d="M19 3v4M17 5h4" stroke="#fff" strokeWidth="1.5"/>
  </svg>,
  <svg key="users" width="36" height="36" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke={C_PURPLE} strokeWidth="1.5"/>
    <circle cx="9" cy="7" r="4" stroke={C_PURPLE} strokeWidth="1.5"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke={C_PINK} strokeWidth="1.5"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke={C_PINK} strokeWidth="1.5"/>
  </svg>,
  <svg key="screen" width="36" height="36" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" stroke={C_PURPLE} strokeWidth="1.5"/>
    <line x1="8" y1="21" x2="16" y2="21" stroke={C_PURPLE} strokeWidth="2"/>
    <line x1="12" y1="17" x2="12" y2="21" stroke={C_PURPLE} strokeWidth="2"/>
    <defs><linearGradient id="wa-g3" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor={C_PINK}/><stop offset="100%" stopColor={C_PURPLE}/></linearGradient></defs>
    <circle cx="12" cy="10" r="4" fill="url(#wa-g3)"/>
    <path d="M10 8l4 4" stroke="#fff" strokeWidth="1.5"/>
  </svg>,
  <svg key="chart" width="36" height="36" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" stroke={C_PURPLE} strokeWidth="2"/>
    <polyline points="17 6 23 6 23 12" stroke={C_PINK} strokeWidth="2"/>
  </svg>,
];

const waIconLabels = ["Award", "Hands-On", "Networking", "Demo", "Analytics"];

function SectionLabel({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:C_PINK, whiteSpace:"nowrap" }}>{children}</span>
      <span style={{ flex:1, height:1.5, background:`linear-gradient(90deg, rgba(240,77,160,0.35), transparent)`, borderRadius:2 }}/>
    </div>
  );
}

function IconPicker({ value = 0, onChange }) {
  return (
    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
      {waIcons.map((icon, i) => (
        <Tooltip key={i} title={waIconLabels[i]}>
          <button type="button" onClick={() => onChange(i)} style={{ width:56, height:56, border:`2px solid ${value === i ? C_PINK : "rgba(139,47,201,0.15)"}`, borderRadius:14, background: value === i ? "rgba(240,77,160,0.07)" : "#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", transition:"all 0.2s", boxShadow: value === i ? `0 0 0 3px rgba(240,77,160,0.15)` : "none" }}>
            {icon}
          </button>
        </Tooltip>
      ))}
    </div>
  );
}

// ── FIXED Rich Text Editor ──
// Fix: uses lastSentHTML ref to break the circular update loop.
// When the form echoes our onChange value back as the new `value` prop,
// useLayoutEffect detects it's our own output and skips the DOM rewrite,
// preventing cursor-jumps and the "raw HTML appearing on every keystroke" bug.
function RichTextEditor({ value, onChange }) {
  const editorRef    = useRef(null);
  const lastSentHTML = useRef(null); // tracks HTML we last emitted via onChange

  useLayoutEffect(() => {
    if (!editorRef.current) return;
    const incoming = value || "";
    // If this incoming value is the same we just sent, it's our own echo — skip.
    // Otherwise it's an external change (page load, form reset) — update the DOM.
    if (incoming === lastSentHTML.current) return;
    if (editorRef.current.innerHTML !== incoming) {
      editorRef.current.innerHTML = incoming;
    }
  }, [value]);

  const emitChange = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    lastSentHTML.current = html; // remember what we're about to echo
    onChange?.(html);
  }, [onChange]);

  const execCmd = (cmd) => {
    document.execCommand(cmd, false, null);
    editorRef.current?.focus();
    emitChange();
  };

  return (
    <div className="rte-outer">
      <div className="rte-bar">
        <button type="button" onMouseDown={e => { e.preventDefault(); execCmd("bold"); }}     className="rte-btn" title="Bold"><strong>B</strong></button>
        <button type="button" onMouseDown={e => { e.preventDefault(); execCmd("italic"); }}   className="rte-btn rte-italic" title="Italic"><em>I</em></button>
        <button type="button" onMouseDown={e => { e.preventDefault(); execCmd("underline"); }} className="rte-btn rte-underline" title="Underline"><u>U</u></button>
        <div className="rte-sep"/>
        <span className="rte-hint">Select text → apply format</span>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        className="rte-editor"
      />
    </div>
  );
}

// ── Default data ──
const defaultTestimonials = [{ name: "", title: "", hospital: "", rating: 5, quote: "", image: [] }];
const defaultWhyAttend = [{ title: "Learn from Top Experts", iconIndex: 0 }, { title: "Hands-On Training", iconIndex: 1 }, { title: "Networking Opportunities", iconIndex: 2 }];
const defaultStatsContent = { labelText: "Conference By The Numbers", titleMain: "Unparalleled Scale and", titleGradient:"Excellence", description: "Skinola International Aesthetics brings together the brightest minds in dermatology and aesthetic medicine." };
const defaultStats = [{ value: "50+", label: "Expert Faculty", desc: "Leading global specialists" }];
const defaultGallery = [{ label: "", date: "", page: "", caption: "", image: [] }];
const defaultOurStoryPara = "<p>Founded with a passion for advancing dermatology and aesthetic medicine...</p>";

const tabNames = { testimonials: "Testimonials", whyAttend: "Why Attend", stats: "Company Stats", ourStory: "Our Story", gallery: "Media Gallery" };

function GalleryPreviewContent({ items }) {
  const [active, setActive]     = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const thumbsRef = useRef(null);

  if (!items || items.length === 0) return <p style={{ textAlign:"center", padding:"40px" }}>No images added to gallery.</p>;

  const go = (idx) => {
    const n = (idx + items.length) % items.length;
    setActive(n);
    if (thumbsRef.current) {
      const el = thumbsRef.current.children[n];
      if (el) el.scrollIntoView({ behavior:"smooth", block:"nearest", inline:"center" });
    }
  };
  const item = items[active] || items[0];

  return (
    <div className="gw">
      <div className="gw-glow1"/><div className="gw-glow2"/>
      <div className="gh">
        <div className="gh-eye"><GalleryIcon/> Media Gallery</div>
        <h2 className="gh-title">News And Corporate <span>Highlights</span></h2>
        <p className="gh-sub">Capturing our journey through press features and moments with distinguished guests.</p>
      </div>

      <div className="gv" onClick={() => setLightbox(true)}>
        <img key={active} src={item.src} alt={item.caption} style={{ animation:"fadeIn 0.35s ease" }}/>
        <div className="counter">{active + 1} / {items.length}</div>
        <div className="zoom-hint"><ZoomIcon/> Click to zoom</div>
        <button className="nav-btn left"  style={{ left:10  }} onClick={e=>{ e.stopPropagation(); go(active-1); }}><ChevLeft/></button>
        <button className="nav-btn right" style={{ right:10 }} onClick={e=>{ e.stopPropagation(); go(active+1); }}><ChevRight/></button>
        <div className="cap">
          <div className="cap-src">{item.label} &nbsp;·&nbsp; {item.date} &nbsp;·&nbsp; {item.page}</div>
          <div className="cap-txt">{item.caption}</div>
        </div>
      </div>

      <div className="ttrack" ref={thumbsRef}>
        {items.map((g, i) => (
          <div key={i} className={`thumb${i===active?" on":""}`} onClick={()=>go(i)}>
            <img src={g.thumb} alt={g.label} loading="lazy"/>
          </div>
        ))}
      </div>

      {lightbox && (
        <div onClick={()=>setLightbox(false)} style={{ position:"fixed", inset:0, zIndex:10000, background:"rgba(15,5,30,0.92)", backdropFilter:"blur(10px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"16px" }}>
          <div onClick={e=>e.stopPropagation()} style={{ position:"relative", maxWidth:900, width:"100%", borderRadius:14, overflow:"hidden", boxShadow:"0 32px 80px rgba(0,0,0,0.6)" }}>
            <div style={{ position:"relative", background:"#000" }}>
              <img src={item.src} alt={item.caption} style={{ width:"100%", maxHeight:"65vh", objectFit:"contain", display:"block" }}/>
              <button onClick={()=>go(active-1)} style={{ position:"absolute", top:"50%", left:10, transform:"translateY(-50%)", background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:"50%", width:38, height:38, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", cursor:"pointer" }}><ChevLeft/></button>
              <button onClick={()=>go(active+1)} style={{ position:"absolute", top:"50%", right:10, transform:"translateY(-50%)", background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:"50%", width:38, height:38, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", cursor:"pointer" }}><ChevRight/></button>
              <button onClick={()=>setLightbox(false)} style={{ position:"absolute", top:10, right:10, background:"rgba(0,0,0,0.5)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:"50%", width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", cursor:"pointer" }}><CloseIcon/></button>
            </div>
            <div style={{ background:"#fff", padding:"14px 18px", borderTop:`2px solid ${C_PINK}` }}>
              <div style={{ fontSize:11, fontWeight:700, color:C_PINK, marginBottom:4 }}>{item.label} · {item.date} · {item.page}</div>
              <div style={{ fontSize:13, color:"#1a0040", lineHeight:1.5 }}>{item.caption}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════
// ── MAIN COMPONENT ──
// ═══════════════════════════════════════════════
export default function WebsiteContentManager() {
  const [form] = Form.useForm();
  const [submitting, setSubmitting]         = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [activeTab, setActiveTab]           = useState("testimonials");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData]       = useState({});
  const [ourStoryImg, setOurStoryImg]       = useState("/images/conference.jpg");
  const [galleryImages, setGalleryImages]   = useState([]);

  // ── Helper: Format DB URL for AntD Upload ──
  const formatForUpload = (url, public_id) => {
    if (!url) return [];
    return [{
      uid: public_id || Math.random().toString(),
      name: "image",
      status: "done",
      url: url,
      image_public_id: public_id
    }];
  };

  // ── 1. Fetch Existing Data ──
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/v1/website-content`, { withCredentials: true });
        const dbData = res.data?.data || {};

        form.setFieldsValue({
          testimonials: dbData.testimonials?.length
            ? dbData.testimonials.map(t => ({ ...t, image: formatForUpload(t.image, t.image_public_id) }))
            : defaultTestimonials,
          whyAttend:    dbData.whyAttend?.length    ? dbData.whyAttend    : defaultWhyAttend,
          statsContent: dbData.statsContent?.titleMain ? dbData.statsContent : defaultStatsContent,
          stats:        dbData.stats?.length         ? dbData.stats        : defaultStats,
          ourStory: {
            para:  dbData.ourStory?.para || defaultOurStoryPara,
            image: formatForUpload(dbData.ourStory?.image, dbData.ourStory?.image_public_id)
          },
          gallery: dbData.gallery?.length
            ? dbData.gallery.map(g => ({ ...g, image: formatForUpload(g.image, g.image_public_id) }))
            : defaultGallery,
        });
      } catch (error) {
        console.error("Error fetching content:", error);
        message.error("Failed to load existing website content.");
      } finally {
        setLoadingInitial(false);
      }
    };
    fetchContent();
  }, [form]);

  // ── Build preview data (handles both URL and local File) ──
  const handlePreview = () => {
    const cv = form.getFieldsValue(true);

    const mappedTestimonials = (cv.testimonials || []).map(t => {
      let avatarSrc = null;
      if (t.image?.length > 0) {
        avatarSrc = t.image[0].originFileObj
          ? URL.createObjectURL(t.image[0].originFileObj)
          : t.image[0].url;
      }
      return { ...t, avatarSrc };
    });

    if (cv.ourStory?.image?.length > 0) {
      const src = cv.ourStory.image[0].originFileObj
        ? URL.createObjectURL(cv.ourStory.image[0].originFileObj)
        : cv.ourStory.image[0].url;
      setOurStoryImg(src);
    }

    const mappedGallery = (cv.gallery || []).map(g => {
      let src = "https://via.placeholder.com/900x600/fcfaff/f04da0?text=No+Image+Uploaded";
      if (g.image?.length > 0) {
        src = g.image[0].originFileObj
          ? URL.createObjectURL(g.image[0].originFileObj)
          : g.image[0].url;
      }
      return { ...g, src, thumb: src };
    });

    setGalleryImages(mappedGallery);
    setPreviewData({ ...cv, testimonials: mappedTestimonials });
    setPreviewVisible(true);
  };

  // ── Helper: Upload Image to Backend ──
  const processImageUpload = async (imageField) => {
    if (!imageField || imageField.length === 0) return { image: "", image_public_id: "" };
    const fileObj = imageField[0];

    if (fileObj.originFileObj) {
      const formData = new FormData();
      formData.append("image", fileObj.originFileObj);
      const res = await axios.post(`${API_BASE}/api/v1/website-content/upload-image`, formData, { withCredentials: true });
      return { image: res.data.data.url, image_public_id: res.data.data.public_id };
    }

    return { image: fileObj.url, image_public_id: fileObj.image_public_id || "" };
  };

  // ── 2. Submit Data ──
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const values = await form.validateFields();

      const finalTestimonials = await Promise.all(
        (values.testimonials || []).map(async (t) => {
          const imgData = await processImageUpload(t.image);
          return { ...t, ...imgData };
        })
      );

      const ourStoryImgData = await processImageUpload(values.ourStory?.image);
      const finalOurStory   = { para: values.ourStory?.para, ...ourStoryImgData };

      const finalGallery = await Promise.all(
        (values.gallery || []).map(async (g) => {
          const imgData = await processImageUpload(g.image);
          return { ...g, ...imgData };
        })
      );

      const payload = {
        testimonials: finalTestimonials,
        whyAttend:    values.whyAttend,
        statsContent: values.statsContent,
        stats:        values.stats,
        ourStory:     finalOurStory,
        gallery:      finalGallery
      };

      await axios.put(`${API_BASE}/api/v1/website-content/update`, payload, { withCredentials: true });
      message.success("Website content updated successfully!");
    } catch (err) {
      console.error(err);
      message.error("Failed to update website content. Please check the form.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <App>
      <div className="lc">
        <div className="mw">

          {/* ── Page Header ── */}
          <div className="header-flex">
            <div>
              <SectionLabel>CMS</SectionLabel>
              <h1 className="sec-title">Website <span style={{ color:C_PINK }}>Content</span></h1>
              <p className="sec-sub">Manage the public-facing content of your landing page here.</p>
            </div>
            <div className="icon-wrap"><GlobeIcon /></div>
          </div>

          <Spin spinning={loadingInitial || submitting} tip={submitting ? "Uploading and Saving..." : "Loading..."}>
            <div className="glass form-wrapper">
              <Form form={form} layout="vertical" onFinish={handleSubmit} className="premium-form" requiredMark={false}>

                <Tabs activeKey={activeTab} onChange={setActiveTab} className="premium-tabs">

                  {/* ═══ TAB 1: TESTIMONIALS ═══ */}
                  <Tabs.TabPane tab="Testimonials" key="testimonials">
                    <p className="tab-desc">Add reviews from doctors to display in the scrolling carousel.</p>
                    <Form.List name="testimonials">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...rest }) => (
                            <div key={key} className="nested-card">
                              <div className="nested-card-header">
                                <h4>Testimonial {name + 1}</h4>
                                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)}>Remove</Button>
                              </div>

                              <Row gutter={16}>
                                <Col xs={24} md={5}>
                                  <Form.Item {...rest} name={[name, "image"]} label="Doctor Photo" valuePropName="fileList" getValueFromEvent={e => e?.fileList}>
                                    <Upload beforeUpload={() => false} listType="picture" maxCount={1} accept="image/*" className="custom-upload">
                                      <div className="upload-btn-inner upload-compact">
                                        <UploadOutlined style={{ fontSize:16, color:C_PINK }}/>
                                        <span>Upload Photo</span>
                                      </div>
                                    </Upload>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={7}>
                                  <Form.Item {...rest} name={[name,"name"]} label="Doctor Name">
                                    <Input placeholder="Dr. Priya Sharma" />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={6}>
                                  <Form.Item {...rest} name={[name,"title"]} label="Specialty / Title">
                                    <Input placeholder="Senior Dermatologist" />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={6}>
                                  <Form.Item {...rest} name={[name,"hospital"]} label="Hospital / Clinic">
                                    <Input placeholder="Apollo Hospitals" />
                                  </Form.Item>
                                </Col>
                              </Row>

                              <Row gutter={16}>
                                <Col xs={24} md={4}>
                                  <Form.Item {...rest} name={[name,"rating"]} label="Rating">
                                    <Select className="custom-select">
                                      <Select.Option value={5}>⭐⭐⭐⭐⭐</Select.Option>
                                      <Select.Option value={4}>⭐⭐⭐⭐</Select.Option>
                                      <Select.Option value={3}>⭐⭐⭐</Select.Option>
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={20}>
                                  <Form.Item {...rest} name={[name,"quote"]} label="Quote / Feedback">
                                    <Input.TextArea rows={2} placeholder="Write the testimonial here..." />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </div>
                          ))}
                          <Button type="dashed" onClick={() => add({ image:[] })} block icon={<PlusOutlined />} className="add-btn">
                            Add Testimonial
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </Tabs.TabPane>

                  {/* ═══ TAB 2: WHY ATTEND ═══ */}
                  <Tabs.TabPane tab="Why Attend" key="whyAttend">
                    <p className="tab-desc">List the key benefits of attending. Choose an icon and enter a title for each item.</p>
                    <Form.List name="whyAttend">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...rest }) => (
                            <div key={key} className="nested-card">
                              <div className="nested-card-header">
                                <h4>Feature {name + 1}</h4>
                                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)}>Remove</Button>
                              </div>
                              <Row gutter={16} align="middle">
                                <Col xs={24} md={16}>
                                  <Form.Item {...rest} name={[name,"title"]} label="Feature Title">
                                    <Input placeholder="e.g. Learn from Top Experts" />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Form.Item {...rest} name={[name,"iconIndex"]} label="Select Icon">
                                <IconPicker />
                              </Form.Item>
                            </div>
                          ))}
                          <Button type="dashed" onClick={() => add({ title:"", iconIndex:0 })} block icon={<PlusOutlined />} className="add-btn">
                            Add Feature
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </Tabs.TabPane>

                  {/* ═══ TAB 3: COMPANY STATS ═══ */}
                  <Tabs.TabPane tab="Company Stats" key="stats">
                    <p className="tab-desc">Edit both the left-side content block and the stat cards on the right.</p>

                    <div className="nested-card" style={{ marginBottom:28 }}>
                      <div className="nested-card-header" style={{ marginBottom:18 }}>
                        <h4>Left Side – Text Content</h4>
                        <span style={{ fontSize:12, color:C_TEXT_MUTED }}>Shown beside the stat cards</span>
                      </div>
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item name={["statsContent","labelText"]} label="Pill Label Text">
                            <Input placeholder="Conference By The Numbers" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name={["statsContent","titleGradient"]} label="Gradient / Highlighted Word">
                            <Input placeholder="Excellence" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24}>
                          <Form.Item name={["statsContent","titleMain"]} label="Title (before highlighted word)">
                            <Input placeholder="Unparalleled Scale and" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24}>
                          <Form.Item name={["statsContent","description"]} label="Description Paragraph">
                            <Input.TextArea rows={3} placeholder="Describe the conference in 2-3 sentences..." />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>

                    <SectionLabel>Stat Cards (Right Side)</SectionLabel>
                    <Form.List name="stats">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...rest }) => (
                            <div key={key} className="nested-card" style={{ padding:"16px" }}>
                              <Row gutter={16} align="middle">
                                <Col xs={24} md={5}>
                                  <Form.Item {...rest} name={[name,"value"]} label="Stat Value" style={{ marginBottom:0 }}>
                                    <Input placeholder="e.g. 50+" />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                  <Form.Item {...rest} name={[name,"label"]} label="Stat Label" style={{ marginBottom:0 }}>
                                    <Input placeholder="e.g. Expert Faculty" />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={9}>
                                  <Form.Item {...rest} name={[name,"desc"]} label="Description" style={{ marginBottom:0 }}>
                                    <Input placeholder="Leading global specialists" />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={2} style={{ display:"flex", justifyContent:"flex-end" }}>
                                  <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} style={{ marginTop:30 }} />
                                </Col>
                              </Row>
                            </div>
                          ))}
                          <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} className="add-btn">
                            Add Statistic
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </Tabs.TabPane>

                  {/* ═══ TAB 4: OUR STORY ═══ */}
                  <Tabs.TabPane tab="Our Story" key="ourStory">
                    <p className="tab-desc">Update the About Us section image and text. Use the editor toolbar to apply <strong>bold</strong>, <em>italic</em>, or <u>underline</u> formatting.</p>
                    <Row gutter={24}>
                      <Col xs={24} md={8}>
                        <Form.Item name={["ourStory","image"]} label="Section Image" valuePropName="fileList" getValueFromEvent={e => e?.fileList}>
                          <Upload beforeUpload={() => false} listType="picture" maxCount={1} accept="image/*" className="custom-upload">
                            <div className="upload-btn-inner">
                              <UploadOutlined style={{ fontSize:"18px", color:C_PINK }}/>
                              <span>Select Image</span>
                            </div>
                          </Upload>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={16}>
                        <Form.Item name={["ourStory","para"]} label="Story Paragraph">
                          <RichTextEditor />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Tabs.TabPane>

                  {/* ═══ TAB 5: MEDIA GALLERY ═══ */}
                  <Tabs.TabPane tab="Media Gallery" key="gallery">
                    <p className="tab-desc">Add news clippings, articles, and corporate highlights to the gallery.</p>
                    <Form.List name="gallery">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...rest }) => (
                            <div key={key} className="nested-card">
                              <div className="nested-card-header">
                                <h4>Gallery Item {name + 1}</h4>
                                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)}>Remove</Button>
                              </div>
                              <Row gutter={16}>
                                <Col xs={24} md={6}>
                                  <Form.Item {...rest} name={[name,"image"]} label="Upload Image" valuePropName="fileList" getValueFromEvent={e => e?.fileList}>
                                    <Upload beforeUpload={() => false} listType="picture" maxCount={1} accept="image/*" className="custom-upload">
                                      <div className="upload-btn-inner" style={{ padding:"10px" }}>
                                        <UploadOutlined style={{ fontSize:"16px", color:C_PINK }}/>
                                        <span>Upload</span>
                                      </div>
                                    </Upload>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={6}>
                                  <Form.Item {...rest} name={[name,"label"]} label="Publication">
                                    <Input placeholder="e.g. Lokasatta" />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={6}>
                                  <Form.Item {...rest} name={[name,"date"]} label="Date">
                                    <Input placeholder="e.g. 12 Mar 2025" />
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={6}>
                                  <Form.Item {...rest} name={[name,"page"]} label="Page / Type">
                                    <Input placeholder="e.g. Page 3" />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Row>
                                <Col span={24}>
                                  <Form.Item {...rest} name={[name,"caption"]} label="Caption / Description">
                                    <Input.TextArea rows={2} placeholder="Write a short description about this press feature..." />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </div>
                          ))}
                          <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} className="add-btn">
                            Add Gallery Item
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </Tabs.TabPane>

                </Tabs>

                {/* ── Form Actions ── */}
                <div className="form-actions">
                  <div style={{ flex:1 }}>
                    <button type="button" className="btn-preview" onClick={handlePreview}>
                      <EyeOutlined /> Preview {tabNames[activeTab]}
                    </button>
                  </div>
                  <button type="submit" className="btn-cta" disabled={submitting || loadingInitial}>
                    <SaveIcon /> Publish All Changes
                  </button>
                </div>
              </Form>
            </div>
          </Spin>
        </div>
      </div>

      {/* <button className="floating-preview-btn" onClick={handlePreview} title="Live Preview"> */}
        {/* <EyeOutlined style={{ fontSize:"20px" }}/> */}
        {/* <span>Preview {tabNames[activeTab]}</span> */}
      {/* </button> */}

      {/* ════════════════════════════════════════
          LIVE PREVIEW DRAWER
      ════════════════════════════════════════ */}
      <Drawer
        title={<span style={{ fontWeight:800, color:C_TEXT_MAIN }}>Previewing: {tabNames[activeTab]}</span>}
        placement="right"
        width="100%"
        onClose={() => setPreviewVisible(false)}
        open={previewVisible}
        bodyStyle={{ padding:0 }}
      >
        <div className="website-preview-root">
          <svg width="0" height="0" style={{ display:"none" }}>
            <defs>
              <linearGradient id="wa-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={C_PINK}/>
                <stop offset="100%" stopColor={C_PURPLE}/>
              </linearGradient>
            </defs>
          </svg>

          {/* ── TESTIMONIALS PREVIEW ── */}
          {activeTab === "testimonials" && (
            <section className="tm-section">
              <div className="tm-header">
                <div className="tm-eyebrow"><span className="tm-dot"/> Testimonials</div>
                <h2 className="tm-title">What Doctors <span className="tm-grad">Say About Us</span></h2>
                <div className="tm-title-line"/>
              </div>
              <div className="tm-stage">
                <div className="tm-track">
                  {(previewData.testimonials || []).slice(0, 3).map((t, i) => (
                    <div key={i} className={`tm-card ${i === 1 || previewData.testimonials.length === 1 ? "tm-card-active" : "tm-card-side"}`}>
                      <div className="tm-card-inner">
                        <div className="tm-quote-icon"><QuoteIcon/></div>
                        <p className="tm-quote">{t?.quote || "No quote provided"}</p>
                        <div className="tm-stars">
                          {[...Array(5)].map((_,idx) => <StarIcon key={idx} filled={idx < (t?.rating || 5)}/>)}
                        </div>
                        <div className="tm-author">
                          {t?.avatarSrc
                            ? <img src={t.avatarSrc} alt={t.name} className="tm-avatar-img"/>
                            : <div className="tm-avatar">{t?.name ? t.name.charAt(0).toUpperCase() : "A"}</div>
                          }
                          <div className="tm-author-info">
                            <div className="tm-author-name">{t?.name || "Author Name"}</div>
                            <div className="tm-author-title">{t?.title || "Title"}</div>
                            <div className="tm-author-hospital">{t?.hospital || "Hospital"}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!previewData.testimonials || previewData.testimonials.length === 0) && (
                    <p style={{ textAlign:"center", width:"100%", gridColumn:"1 / -1" }}>No testimonials added.</p>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* ── WHY ATTEND PREVIEW ── */}
          {activeTab === "whyAttend" && (
            <section className="wa-section">
              <div className="wa-inner">
                <div className="wa-header-container">
                  <div className="wa-line wa-line-left"/>
                  <h2 className="wa-title">Why Attend Skinola?</h2>
                  <div className="wa-line wa-line-right"/>
                </div>
                <div className="wa-grid">
                  {(previewData.whyAttend || []).map((feature, index) => {
                    const iconIdx = feature?.iconIndex ?? (index % waIcons.length);
                    return (
                      <div className="wa-item-wrapper" key={index}>
                        <div className="wa-item">
                          <div className="wa-icon-container">
                            <BlobBg/>
                            <div className="wa-icon">{waIcons[iconIdx]}</div>
                          </div>
                          <h4 className="wa-item-title">{feature?.title || "New Feature"}</h4>
                        </div>
                        {index < (previewData.whyAttend.length - 1) && <div className="wa-divider"/>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* ── STATS PREVIEW ── */}
          {activeTab === "stats" && (() => {
            const sc = previewData.statsContent || defaultStatsContent;
            return (
              <section className="st-full-bg">
                <div className="st-container">
                  <div className="st-wrapper">
                    <div className="st-text-block">
                      <div className="st-label">
                        <svg width="16" height="16" fill="none" stroke={C_PINK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                          <polyline points="17 6 23 6 23 12"/>
                        </svg>
                        {sc.labelText || "Conference By The Numbers"}
                      </div>
                      <h1 className="st-title">
                        {sc.titleMain || "Unparalleled Scale and"}{" "}
                        <span className="st-gradient"><strong>{sc.titleGradient || "Excellence"}</strong></span>
                      </h1>
                      <p className="st-desc">{sc.description || ""}</p>
                    </div>
                    <div className="st-grid-block">
                      <div className="st-grid">
                        {(previewData.stats || []).map((stat, i) => (
                          <div key={i} className="st-card">
                            <div className="st-val">{stat?.value || "0"}</div>
                            <h3 className="st-card-heading">{stat?.label || "Label"}</h3>
                            <p className="st-card-sub">{stat?.desc || "Description"}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            );
          })()}

          {/* ── OUR STORY PREVIEW ──
              FIX: replaced <p dangerouslySetInnerHTML> with <div dangerouslySetInnerHTML>.
              A <p> cannot contain block-level children (<p>, <div>) — browsers
              auto-correct the invalid nesting and the output breaks. Using <div>
              with class os-rich-text matches the frontend component exactly. -->
          */}
          {activeTab === "ourStory" && (
            <section className="os-section">
              <div className="os-inner">
                <div className="os-header os-mobile-header">
                  <h2 className="os-title">Our <span className="os-gradient-text">Story</span></h2>
                  <div className="os-title-line"/>
                </div>
                <div className="os-grid">
                  <div className="os-img-wrap">
                    <img src={ourStoryImg} alt="Our Story"/>
                  </div>
                  <div className="os-content">
                    <div className="os-header os-desktop-header">
                      <div className="os-eyebrow"><span className="os-eyebrow-dot"/> About Us</div>
                      <h2 className="os-title">Our <span className="os-gradient-text">Story</span></h2>
                      <div className="os-title-line"/>
                    </div>
                    {/* FIXED: use <div> not <p> so block children render correctly */}
                    <div
                      className="os-rich-text"
                      dangerouslySetInnerHTML={{ __html: previewData.ourStory?.para || "" }}
                    />
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ── GALLERY PREVIEW ── */}
          {activeTab === "gallery" && (
            <section className="gl-section">
              <GalleryPreviewContent items={galleryImages}/>
            </section>
          )}

        </div>
      </Drawer>

      <style>{`
        * { box-sizing: border-box; }
        .lc { font-family:'Segoe UI',sans-serif; background:${C_SOFT_BG}; min-height:100vh; padding:40px 0 100px; }
        .mw { max-width:1000px; margin:0 auto; padding:0 24px; }
        .header-flex { display:flex; align-items:flex-end; justify-content:space-between; margin-bottom:32px; }
        .sec-title { font-size:clamp(28px,4vw,38px); font-weight:800; color:${C_TEXT_MAIN}; letter-spacing:-0.02em; line-height:1.15; margin:0 0 8px; }
        .sec-sub { font-size:15px; color:${C_TEXT_MUTED}; margin:0; }
        .icon-wrap { width:54px; height:54px; background:rgba(240,77,160,0.1); border:1px solid rgba(240,77,160,0.2); border-radius:16px; display:flex; align-items:center; justify-content:center; color:${C_PINK}; }
        .glass { background:#fff; border-radius:20px; padding:24px 32px; border:1px solid rgba(139,47,201,0.08); box-shadow:0 10px 40px rgba(0,0,0,0.04); }
        .tab-desc { color:${C_TEXT_MUTED}; margin-bottom:24px; font-size:14.5px; }
        .premium-tabs .ant-tabs-nav { margin-bottom:24px !important; }
        .premium-tabs .ant-tabs-tab { font-weight:600; font-size:15px; color:${C_TEXT_MUTED}; }
        .premium-tabs .ant-tabs-tab-active .ant-tabs-tab-btn { color:${C_PINK} !important; }
        .premium-tabs .ant-tabs-ink-bar { background:${C_PINK} !important; height:3px !important; }
        .nested-card { background:#faf8fc; border:1px solid rgba(139,47,201,0.12); border-radius:14px; padding:20px; margin-bottom:20px; }
        .nested-card-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; border-bottom:1px solid rgba(139,47,201,0.08); padding-bottom:12px; }
        .nested-card-header h4 { margin:0; font-size:15px; font-weight:700; color:${C_TEXT_MAIN}; }
        .add-btn { height:44px; border-radius:10px; border-color:rgba(139,47,201,0.3); color:${C_PURPLE}; font-weight:600; background:rgba(139,47,201,0.03); margin-top:8px; }
        .add-btn:hover { border-color:${C_PURPLE}; color:${C_PURPLE}; background:rgba(139,47,201,0.08); }
        .custom-upload .ant-upload-select { display:block !important; width:100%; }
        .upload-btn-inner { display:flex; align-items:center; justify-content:center; gap:10px; padding:16px; background:#fff0f8; border:1.5px dashed ${C_PINK}; border-radius:12px; color:${C_TEXT_MAIN}; font-weight:600; font-size:14.5px; cursor:pointer; transition:all 0.2s; }
        .upload-compact { padding:10px 14px !important; font-size:13px !important; }

        /* ── Rich Text Editor ── */
        .rte-outer { border:1px solid #d9d9d9; border-radius:8px; overflow:hidden; }
        .rte-bar { display:flex; align-items:center; gap:6px; padding:8px 12px; background:#fafafa; border-bottom:1px solid #ebebeb; }
        .rte-btn { width:30px; height:30px; border:1px solid #d9d9d9; border-radius:6px; background:#fff; font-size:14px; cursor:pointer; transition:all 0.15s; display:flex; align-items:center; justify-content:center; }
        .rte-btn:hover { background:rgba(240,77,160,0.08); border-color:${C_PINK}; color:${C_PINK}; }
        .rte-italic { font-style:italic; }
        .rte-underline { text-decoration:underline; }
        .rte-sep { width:1px; height:20px; background:#e0e0e0; margin:0 4px; }
        .rte-hint { font-size:11.5px; color:${C_TEXT_MUTED}; margin-left:4px; }
        .rte-editor { min-height:130px; padding:14px; font-size:14.5px; line-height:1.8; color:${C_TEXT_MAIN}; outline:none; }
        .rte-editor:focus { background:#fdfbff; }

        /* ── Form Actions ── */
        .form-actions { display:flex; align-items:center; justify-content:flex-end; gap:16px; margin-top:40px; padding-top:24px; border-top:1px solid rgba(139,47,201,0.08); }
        .btn-cta { display:inline-flex; align-items:center; gap:8px; padding:14px 28px; background:${C_GRAD}; border:none; border-radius:50px; color:#fff; font-size:14.5px; font-weight:700; cursor:pointer; box-shadow:0 10px 24px rgba(240,77,160,0.3); transition:all 0.25s; }
        .btn-cta:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 14px 28px rgba(240,77,160,0.4); }
        .btn-preview { display:inline-flex; align-items:center; gap:8px; padding:14px 24px; background:#fff; border:1px solid ${C_PINK}; border-radius:50px; color:${C_PINK}; font-size:14.5px; font-weight:700; cursor:pointer; transition:all 0.2s; }
        .btn-preview:hover { background:${C_PINK}; color:#fff; }
        .floating-preview-btn { position:fixed; bottom:30px; right:30px; z-index:1000; background:linear-gradient(135deg,#c026d3,#6b19a3); color:white; border:none; border-radius:50px; padding:14px 24px; box-shadow:0 6px 20px rgba(192,38,211,0.4); cursor:pointer; display:flex; align-items:center; gap:10px; font-weight:700; font-size:15px; transition:transform 0.2s,box-shadow 0.2s; }
        .floating-preview-btn:hover { transform:translateY(-3px); box-shadow:0 10px 24px rgba(192,38,211,0.5); }
        @media(max-width:768px) {
          .form-actions { flex-direction:column-reverse; align-items:stretch; }
          .btn-cta,.btn-preview { width:100%; justify-content:center; margin-bottom:8px; }
          .floating-preview-btn { bottom:20px; right:20px; padding:12px 20px; }
        }

        /* ════════════════════════════════════════
           PREVIEW STYLES
        ════════════════════════════════════════ */
        .website-preview-root { font-family:'Segoe UI',system-ui,sans-serif; }
        .website-preview-root section { padding:80px 0; overflow:hidden; width:100%; min-height:100vh; display:flex; flex-direction:column; justify-content:center; align-items:center; }
        @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
        @keyframes pulseDot { 0%,100%{box-shadow:0 0 0 0 rgba(240,77,160,0.4)} 50%{box-shadow:0 0 0 6px rgba(240,77,160,0)} }

        /* Testimonials */
        .tm-section { background:linear-gradient(160deg,#faf5ff 0%,#ffffff 50%,#fdf4ff 100%); }
        .tm-header { text-align:center; display:flex; flex-direction:column; align-items:center; margin-bottom:50px; }
        .tm-eyebrow { display:inline-flex; align-items:center; gap:8px; background:rgba(240,77,160,0.08); border:1.5px solid rgba(107,25,163,0.15); color:#8B2FC9; font-size:12px; font-weight:700; text-transform:uppercase; padding:6px 16px; border-radius:50px; margin-bottom:16px; }
        .tm-dot { width:8px; height:8px; border-radius:50%; background:#c026d3; }
        .tm-title { font-size:clamp(28px,4vw,44px); font-weight:800; color:#1a0040; margin:0 0 10px; line-height:1.2; letter-spacing:-0.02em; }
        .tm-grad { background:linear-gradient(90deg,#F04DA0 0%,#c066f0 50%,#F04DA0 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
        .tm-title-line { width:60px; height:4px; border-radius:4px; background:linear-gradient(90deg,#c026d3,#8B2FC9); margin-bottom:10px; }
        .tm-stage { width:90vw; max-width:1350px; margin:0 auto; }
        .tm-track { display:grid; grid-template-columns:1fr 1.15fr 1fr; gap:16px; align-items:center; }
        .tm-card { border-radius:22px; transition:transform 0.4s,opacity 0.4s; }
        .tm-card-side { opacity:0.55; transform:scale(0.93); }
        .tm-card-active { transform:scale(1); opacity:1; box-shadow:0 16px 52px rgba(107,25,163,0.16); }
        .tm-card-inner { background:white; border:1.5px solid rgba(107,25,163,0.1); border-radius:22px; padding:22px; display:flex; flex-direction:column; gap:18px; }
        .tm-card-active .tm-card-inner { border-color:rgba(192,38,211,0.25); background:linear-gradient(160deg,#ffffff,#fdf6ff); }
        .tm-quote { font-size:14.5px; color:#5a5a72; line-height:1.8; margin:0; }
        .tm-stars { display:flex; gap:3px; }
        .tm-author { display:flex; align-items:center; gap:14px; padding-top:16px; border-top:1.5px solid rgba(107,25,163,0.07); }
        .tm-avatar { width:48px; height:48px; border-radius:50%; background:linear-gradient(135deg,#c026d3,#8B2FC9); display:flex; align-items:center; justify-content:center; font-size:14px; font-weight:800; color:white; flex-shrink:0; }
        .tm-avatar-img { width:48px; height:48px; border-radius:50%; object-fit:cover; flex-shrink:0; border:2px solid rgba(192,38,211,0.25); }
        .tm-author-name { font-size:14px; font-weight:800; color:#4a1171; }
        .tm-author-title { font-size:12.5px; font-weight:600; color:#8B2FC9; }
        .tm-author-hospital { font-size:11.5px; color:#9a8aaa; }

        /* Why Attend */
        .wa-section { background:#f6effa; }
        .wa-inner { width:90vw; max-width:1300px; margin:0 auto; }
        .wa-header-container { display:flex; align-items:center; justify-content:center; gap:24px; margin-bottom:40px; }
        .wa-title { font-size:clamp(26px,4vw,44px); font-weight:800; color:#1a0040; letter-spacing:-0.02em; white-space:nowrap; }
        .wa-line { flex-grow:1; height:2px; max-width:250px; }
        .wa-line-left { background:linear-gradient(270deg,rgba(139,47,201,0.6) 0%,transparent 100%); }
        .wa-line-right { background:linear-gradient(90deg,rgba(139,47,201,0.6) 0%,transparent 100%); }
        .wa-grid { display:flex; justify-content:space-between; flex-wrap:wrap; }
        .wa-item-wrapper { flex:1; display:flex; align-items:center; min-width:100px; }
        .wa-item { display:flex; flex-direction:column; align-items:center; text-align:center; padding:16px 12px; }
        .wa-icon-container { position:relative; width:86px; height:86px; display:flex; align-items:center; justify-content:center; margin-bottom:16px; }
        .wa-blob { position:absolute; width:125%; height:125%; z-index:0; }
        .wa-icon { position:relative; z-index:1; }
        .wa-item-title { font-size:16px; font-weight:700; color:#3a2060; margin:0; }
        .wa-divider { width:1.5px; height:60px; background:linear-gradient(180deg,transparent,rgba(139,47,201,0.15),transparent); }

        /* Stats */
        .st-full-bg { background-color:#e7cbe7; width:100%; }
        .st-container { width:90vw; max-width:1350px; margin:0 auto; }
        .st-wrapper { display:grid; grid-template-columns:1fr 1fr; gap:56px; align-items:center; }
        .st-text-block { display:flex; flex-direction:column; }
        .st-label { display:inline-flex; align-items:center; gap:8px; background:white; color:#6b19a3; font-size:13.5px; font-weight:700; padding:7px 18px; border-radius:50px; border:1.5px solid rgba(107,25,163,0.12); width:fit-content; margin-bottom:20px; letter-spacing:0.3px; text-transform:uppercase; box-shadow:0 4px 12px rgba(107,25,163,0.05); }
        .st-title { font-size:clamp(24px,5vw,44px); font-weight:800; color:#1a0040; margin:0 0 10px; line-height:1.2; letter-spacing:-0.02em; }
        .st-gradient { background:linear-gradient(90deg,#F04DA0 0%,#c066f0 50%,#F04DA0 100%); background-size:200% auto; -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; animation:shimmer 4s linear infinite; }
        .st-desc { font-size:16px; color:#4a4a5e; line-height:1.75; margin:0; max-width:95%; }
        .st-grid { display:grid; grid-template-columns:1fr 1fr; gap:24px; }
        .st-card { background:white; border-radius:22px; border:1.5px solid rgba(107,25,163,0.05); box-shadow:0 10px 30px rgba(107,25,163,0.08); padding:32px 24px; display:flex; flex-direction:column; transition:transform 0.3s,box-shadow 0.3s; }
        .st-card:hover { transform:translateY(-6px); box-shadow:0 16px 40px rgba(107,25,163,0.12); }
        .st-val { font-size:42px; font-weight:800; background:linear-gradient(135deg,#c026d3,#8B2FC9); -webkit-background-clip:text; -webkit-text-fill-color:transparent; line-height:1; margin-bottom:12px; }
        .st-card-heading { font-size:17px; font-weight:800; color:#4a1171; margin:0 0 6px; }
        .st-card-sub { font-size:13.5px; color:#7a6a8a; margin:0; }

        /* Our Story */
        .os-section { background:linear-gradient(135deg,#fcfafc 0%,#f6effa 100%); width:100%; }
        .os-inner { position:relative; z-index:1; width:90vw; max-width:1350px; margin:0 auto; }
        .os-grid { display:flex; align-items:center; gap:60px; }
        .os-header { display:flex; flex-direction:column; margin-bottom:24px; }
        .os-mobile-header { display:none; }
        .os-desktop-header { display:flex; align-items:flex-start; }
        .os-eyebrow { display:inline-flex; align-items:center; gap:8px; background:rgba(240,77,160,0.08); border:1px solid rgba(240,77,160,0.2); color:${C_PINK}; font-size:12px; font-weight:700; letter-spacing:0.15em; text-transform:uppercase; padding:6px 16px 6px 12px; border-radius:50px; margin-bottom:16px; }
        .os-eyebrow-dot { width:6px; height:6px; border-radius:50%; background:${C_PINK}; animation:pulseDot 2s infinite; }
        .os-title { font-size:clamp(28px,4vw,46px); font-weight:800; color:#1a0040; margin:0 0 16px; line-height:1.15; letter-spacing:-0.01em; }
        .os-gradient-text { background:linear-gradient(90deg,${C_PINK} 0%,#c066f0 50%,${C_PINK} 100%); background-size:200% auto; -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; animation:shimmer 4s linear infinite; }
        .os-title-line { width:60px; height:4px; border-radius:4px; background:${C_GRAD}; }
        .os-img-wrap { flex:0 0 45%; aspect-ratio:4/3; border-radius:24px; overflow:hidden; box-shadow:0 20px 50px rgba(107,25,163,0.12),0 6px 18px rgba(107,25,163,0.06); }
        .os-img-wrap img { width:100%; height:100%; object-fit:cover; display:block; }
        .os-content { flex:1; min-width:0; }

        /* FIXED: os-rich-text covers both <p> and <div> (contentEditable emits <div> on Enter) */
        .os-rich-text { font-size:clamp(15px,1.4vw,17px); color:#6a5a7a; line-height:1.8; font-weight:500; }
        .os-rich-text p,
        .os-rich-text div { margin:0 0 12px; }
        .os-rich-text p:last-child,
        .os-rich-text div:last-child { margin-bottom:0; }
        .os-rich-text strong, .os-rich-text b { font-weight:700; color:#1a0040; }
        .os-rich-text em  { font-style:italic; }
        .os-rich-text u   { text-decoration:underline; }

        /* Gallery */
        .gl-section { width:100%; display:flex; align-items:center; justify-content:center; background:#f6effa; }
        .gw { background:#fff; padding:36px 32px 40px; width:90vw; max-width:1000px; border-radius:16px; position:relative; overflow:hidden; box-shadow:0 20px 50px rgba(107,25,163,0.05); }
        .gw-glow1 { position:absolute; top:-100px; right:-100px; width:300px; height:300px; border-radius:50%; background:radial-gradient(circle,rgba(139,47,201,0.07) 0%,transparent 70%); }
        .gw-glow2 { position:absolute; bottom:-80px; left:-80px; width:260px; height:260px; border-radius:50%; background:radial-gradient(circle,rgba(240,77,160,0.06) 0%,transparent 70%); }
        .gh { text-align:center; margin-bottom:24px; position:relative; z-index:1; }
        .gh-eye { display:inline-flex; align-items:center; gap:8px; background:rgba(240,77,160,0.08); border:1.5px solid rgba(107,25,163,0.15); color:#8B2FC9; font-size:12px; font-weight:700; text-transform:uppercase; padding:6px 16px; border-radius:50px; margin-bottom:16px; }
        .gh-title { font-size:clamp(22px,3vw,36px); font-weight:800; color:#1a0040; margin:0 0 10px; }
        .gh-title span { background:linear-gradient(90deg,#F04DA0,#c066f0,#F04DA0); -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
        .gh-sub { font-size:13.5px; color:#6b5080; max-width:440px; margin:0 auto; line-height:1.65; }
        .gv { position:relative; border-radius:14px; overflow:hidden; background:#f5eefa; border:1px solid rgba(139,47,201,0.12); margin-bottom:12px; cursor:zoom-in; display:flex; align-items:center; justify-content:center; min-height:400px; }
        .gv img { width:100%; max-height:520px; object-fit:contain; display:block; transition:transform 0.4s; }
        .gv:hover img { transform:scale(1.02); }
        .nav-btn { position:absolute; top:50%; transform:translateY(-50%); background:rgba(255,255,255,0.88); backdrop-filter:blur(6px); border:1px solid rgba(139,47,201,0.18); border-radius:50%; width:40px; height:40px; display:flex; align-items:center; justify-content:center; color:#1a0040; cursor:pointer; transition:all 0.2s; z-index:5; }
        .nav-btn:hover { background:${C_PINK}; color:#fff; border-color:${C_PINK}; }
        .counter { position:absolute; top:12px; left:12px; background:rgba(255,255,255,0.88); backdrop-filter:blur(6px); border:1px solid rgba(139,47,201,0.15); font-size:11px; font-weight:700; color:#4a2070; padding:3px 10px; border-radius:50px; z-index:5; letter-spacing:0.06em; }
        .zoom-hint { position:absolute; top:12px; right:12px; background:rgba(255,255,255,0.88); backdrop-filter:blur(6px); border:1px solid rgba(139,47,201,0.15); color:#4a2070; font-size:11px; font-weight:600; padding:4px 10px; border-radius:50px; display:flex; align-items:center; gap:5px; pointer-events:none; z-index:5; }
        .cap { position:absolute; bottom:0; left:0; right:0; background:linear-gradient(0deg,rgba(255,255,255,0.97) 0%,rgba(255,255,255,0.6) 65%,transparent 100%); padding:28px 16px 14px; }
        .cap-src { font-size:11px; font-weight:700; color:${C_PINK}; letter-spacing:0.05em; margin-bottom:4px; display:flex; align-items:center; gap:6px; }
        .cap-src::before { content:""; display:inline-block; width:14px; height:2px; border-radius:2px; background:${C_GRAD}; }
        .cap-txt { font-size:13px; color:#1a0040; line-height:1.45; font-weight:500; }
        .ttrack { display:flex; gap:8px; overflow-x:auto; scroll-behavior:smooth; padding-bottom:4px; position:relative; z-index:1; scrollbar-width:none; }
        .ttrack::-webkit-scrollbar { display:none; }
        .thumb { flex-shrink:0; width:90px; height:60px; border-radius:8px; overflow:hidden; cursor:pointer; border:2px solid transparent; transition:all 0.25s; }
        .thumb.on { border-color:${C_PINK}; box-shadow:0 0 0 1px rgba(240,77,160,0.35),0 4px 12px rgba(240,77,160,0.2); transform:translateY(-2px); }
        .thumb img { width:100%; height:100%; object-fit:cover; display:block; transition:transform 0.3s; }

        /* Responsive */
        @media(max-width:900px) {
          .tm-track { grid-template-columns:1fr; }
          .tm-card-side { display:none; }
          .wa-item-wrapper { flex:0 0 33%; flex-direction:column; }
          .wa-divider { display:none; }
          .st-wrapper { grid-template-columns:1fr; }
          .os-grid { flex-direction:column; }
          .os-img-wrap { width:100%; aspect-ratio:16/9; }
          .gv { min-height:220px; }
          .gv img { max-height:400px; }
        }
        @media(max-width:767px) {
          .os-section { padding:44px 0; }
          .os-mobile-header { display:flex; align-items:center; text-align:center; margin-bottom:20px; }
          .os-desktop-header { display:none; }
          .os-mobile-header .os-title { font-size:22px; text-align:center; }
          .os-mobile-header .os-title-line { align-self:center; }
          .os-rich-text { font-size:14.5px; line-height:1.75; }
          .os-rich-text p, .os-rich-text div { margin:0 0 10px; }
        }
        @media(max-width:768px) {
          .nav-btn { width:36px; height:36px; }
          .zoom-hint { display:none; }
          .thumb { width:74px; height:50px; }
        }
      `}</style>
    </App>
  );
}